import * as turf from '@turf/turf'
import { parcelApiKey } from '../../../env-var.js'
import { get } from 'axios'
import { parse as parseWkt } from 'wkt'

class CachedParcel {
  constructor (parcelAsFeatureCollection) {
    this.parcelAsFeatureCollection = parcelAsFeatureCollection
    this.bbox = turf.bbox(parcelAsFeatureCollection)
  }
}

class ParcelCache {
  constructor () {
    this.cachedParcelsByRobustId = new Map()
  }

  get #cachedParcelsArray () {
    return Array.from(this.cachedParcelsByRobustId).map(([_, val]) => val)
  }

  #getRobustId = (parcelAsFeatureCollection) => {
    return parcelAsFeatureCollection?.features[0]?.properties?.fields?.robust_id ?? null
  }

  #getFeature = (cachedParcel) => {
    return cachedParcel?.parcelAsFeatureCollection?.features[0] ?? null
  }

  #point = (coordinate) => {
    return turf.point([coordinate.longitude, coordinate.latitude])
  }

  #getParcelsInRangeByBoundingBox = (coordinate) => {
    return this.#cachedParcelsArray.filter(cachedParcel => {
      const bbox = turf.bboxPolygon(cachedParcel.bbox)
      return turf.booleanPointInPolygon(this.#point(coordinate), bbox)
    })
  }

  #isInsideParcel = (coordinate, cachedParcel) => {
    return turf.booleanPointInPolygon(this.#point(coordinate), this.#getFeature(cachedParcel))
  }

  #result = (cachedParcel) => {
    return {
      parcels: cachedParcel.parcelAsFeatureCollection
    }
  }

  addParcel = (parcelAsFeatureCollection) => {
    if (parcelAsFeatureCollection.features) {
      const cachedParcel = new CachedParcel(parcelAsFeatureCollection)
      this.cachedParcelsByRobustId.set(this.#getRobustId(parcelAsFeatureCollection), cachedParcel)
    }
  }

  getParcelForCoordinate = (coordinate) => {
    const cachedParcel = this.#getParcelsInRangeByBoundingBox(coordinate)
      .find(cachedParcel => this.#isInsideParcel(coordinate, cachedParcel))
    return cachedParcel ? this.#result(cachedParcel) : null
  }

  getParcelByRobustId = (robustId) => {
    const cachedParcel = this.cachedParcelsByRobustId.get(robustId)
    return cachedParcel ? this.#result(cachedParcel) : null
  }
}

export const parcelCache = new ParcelCache()

export const getParcelApiKey = () => {
  return parcelApiKey.includes('VUE') ? process.env.zzQmguu7mQ : parcelApiKey
}

export const getParcelVectorTilesUrl = () =>
  `https://reportallusa.com/api/rest_services/client=${getParcelApiKey()}/outFields=latitude,longitude/ParcelsVectorTile/MapBoxVectorTileServer/tile/{z}/{x}/{y}.mvt`

const getUrlForParcelDetailsByRobustId = (robustId) => {
  const base = 'https://reportallusa.com/api/parcels'
  const parcelApiVersion = 9
  const parcelApiKey = getParcelApiKey()
  if (!parcelApiKey) {
    console.warn('Parcel API key unavailable. Proceeding without parcel data.')
    return
  }
  const path = `${base}?client=${parcelApiKey}&v=${parcelApiVersion}&robust_id=${robustId}&si_srid=4326`
  return path
}

export async function getParcelDetailsByRobustId (robustId) {
  let result = parcelCache.getParcelByRobustId(robustId)
  if (result) return result

  result = await fetchParcelFromServiceByRobustId(robustId)
  if (result) parcelCache.addParcel(result.parcels)

  return result
}

async function fetchParcelFromServiceByRobustId (robustId) {
  const url = getUrlForParcelDetailsByRobustId(robustId)
  const parcelServiceResponse = await get(url)
  return formatParcelData(parcelServiceResponse)
}

export const searchCoordinatesWithParcelService = async ({ latitude, longitude }) => {
  const base = 'https://reportallusa.com/api/parcels'
  const parcelApiVersion = 9
  const parcelApiKey = getParcelApiKey()
  if (!parcelApiKey) {
    console.warn('Parcel API key unavailable. Proceeding without parcel data.')
    return
  }
  const path = `${base}?client=${parcelApiKey}&v=${parcelApiVersion}&spatial_intersect=POINT(${longitude}%20${latitude})&si_srid=4326`

  const parcelServiceResponse = await get(path)
  return formatParcelData(parcelServiceResponse)
}

function formatParcelData (parcelServiceResponse) {
  const { data } = parcelServiceResponse
  const { results } = data
  const parcelsFound = results && results.length > 0
  if (parcelsFound) {
    const firstParcel = results[0]

    if (!firstParcel.geom_as_wkt) {
      return undefined
    }

    const firstParcelAsMultiPolygon = parseWkt(firstParcel.geom_as_wkt)
    const geojson = {
      parcels: {
        type: 'FeatureCollection',
        features: [{
          type: 'Feature',
          geometry: firstParcelAsMultiPolygon,
          properties: {
            fields: {
              ...firstParcel
            }
          }
        }]
      }
    }
    return geojson
  }
  return undefined
}
